// frontend_react/src/pages/restaurant/OrderScreen.jsx

import React, { useState, useMemo } from 'react'
import PayModal from '../../components/paymodal/index.jsx'
import Item from '../../components/restaurant/Item'
import ItemList from '../../components/restaurant/ItemList'
import '../../styles/restaurant.css'

function OrderScreen({ setScreen, tableNumber, order, setOrders, fermerTable, refreshOrders, apiFetch, productMap, allCategories }) {
  const [modalProduct, setModalProduct] = useState(null)
  const [fieldValues, setFieldValues] = useState({})

  const cart = order.cart
  const preparedCart = order.preparedCart
  const orderUuid = order.uuid

  const setCart = (updater) => {
    setOrders(prev => ({
      ...prev,
      [tableNumber]: {
        ...prev[tableNumber],
        cart: typeof updater === "function" ? updater(prev[tableNumber].cart) : updater
      }
    }))
  }

  const [showPayModal, setShowPayModal] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const [receiptUuid, setReceiptUuid] = useState(null)

  const payModalItems = useMemo(() => {
    if (!showPayModal) return null

    const allItems = [...preparedCart, ...cart]

    return allItems.map(item => ({
      id: item.uuid || item.item_backend_uuid,
      name: item.name || item.designation || item.label,
      price: Number(item.price ?? 0) / 100,
      qty: Number(item.quantite ?? item.quantity ?? 1),
      discounts: [],
      _uuid: item.item_backend_uuid || item.uuid,
    })).filter(i => i.qty > 0 && i.price >= 0)
  }, [showPayModal, cart, preparedCart])

  // ---- PRÉPARER ----
  const preparerLePanier = async () => {
    if (!orderUuid) return
    try {
      await apiFetch(`/orders/${orderUuid}/prepare`, { method: "POST" })
      await refreshOrders()
    } catch (err) { console.error(err) }
  }

  // ---- AJOUTER ----
  const ajouterAuPanier = async (product) => {
    if (product.fields?.some(f => f.category === "required")) {
      setModalProduct(product)
      const defaults = {}
      product.fields.forEach(f => { defaults[f.field_name] = f.default ?? null })
      setFieldValues(defaults)
      return
    }

    const existe = cart.find(i => i.uuid === product.uuid)
    if (existe && existe.item_backend_uuid && orderUuid) {
      try {
        await apiFetch(`/orders/${orderUuid}/items/${existe.item_backend_uuid}`, {
          method: "PUT",
          body: JSON.stringify({ quantity: existe.quantite + 1 }),
        })
        await refreshOrders()
      } catch (err) { console.error(err) }
      return
    }

    if (orderUuid) {
      try {
        await apiFetch(`/orders/${orderUuid}/items`, {
          method: "POST",
          body: JSON.stringify({
            menu_item_uuid: product.uuid,
            quantity: 1,
            price: product.price / 100,
            filled_fields: null,
          }),
        })
        await refreshOrders()
      } catch (err) { console.error(err) }
    }
  }

  // ---- SUPPRIMER ----
  const supprimerDuPanier = async (product) => {
    if (!product.item_backend_uuid || !orderUuid) return
    if (product.quantite === 1) {
      try {
        await apiFetch(`/orders/${orderUuid}/items/${product.item_backend_uuid}`, { method: "DELETE" })
        await refreshOrders()
      } catch (err) { console.error(err) }
    } else {
      try {
        await apiFetch(`/orders/${orderUuid}/items/${product.item_backend_uuid}`, {
          method: "PUT",
          body: JSON.stringify({ quantity: product.quantite - 1 }),
        })
        await refreshOrders()
      } catch (err) { console.error(err) }
    }
  }

  // ---- VALIDER MODAL ----
  const validerModal = async () => {
    if (!modalProduct || !orderUuid) return
    if (modalProduct.item_backend_uuid) {
      try {
        await apiFetch(`/orders/${orderUuid}/items/${modalProduct.item_backend_uuid}`, {
          method: "PUT",
          body: JSON.stringify({ filled_fields: { ...fieldValues } }),
        })
        await refreshOrders()
      } catch (err) { console.error(err) }
    } else {
      try {
        await apiFetch(`/orders/${orderUuid}/items`, {
          method: "POST",
          body: JSON.stringify({
            menu_item_uuid: modalProduct.uuid,
            quantity: 1,
            price: modalProduct.price / 100,
            filled_fields: { ...fieldValues },
          }),
        })
        await refreshOrders()
      } catch (err) { console.error(err) }
    }
    setModalProduct(null)
  }

  // ---- Payment Modal ----

  const handleOpenPayment = async () => {
    setPaymentError(null)
    setReceiptUuid(null)

    if (!orderUuid) {
      setPaymentError("Commande introuvable — impossible d'ouvrir le règlement.")
      return
    }

    if (cart.length === 0 && preparedCart.length === 0) {
      setPaymentError("Aucun article à régler.")
      return
    }

    try {
      const res = await apiFetch(`/orders/${orderUuid}/checkout`, {
        method: "POST",
      })

      const uuid =
        res?.receipt_uuid ||
        res?.receiptUuid ||
        res?.uuid ||
        res?.receipt?.uuid ||
        res?.data?.receipt_uuid ||
        res?.data?.uuid ||
        res?.data?.receipt?.uuid

      if (!uuid) {
        console.error("Receipt UUID introuvable :", res)
        setPaymentError("Ticket de caisse introuvable — règlement impossible.")
        return
      }

      setReceiptUuid(uuid)
      setShowPayModal(true)
    } catch (err) {
      console.error(err)
      setPaymentError(err?.message ?? "Erreur lors de la création du ticket.")
    }
  }

  const handleClosePayment = () => {
    setShowPayModal(false)
    setPaymentError(null)
    setReceiptUuid(null)
  }

  const handlePaid = async () => {
    setShowPayModal(false)
    setPaymentError(null)
    setReceiptUuid(null)

    try {
      await refreshOrders()
      fermerTable(tableNumber)
    } catch (err) {
      console.error(err)
    }
  }

  const viderLePanier = () => setCart([])

  const cartTotal = cart.reduce((t, i) => t + (i.price * i.quantite), 0) / 100
  const preparedTotal = preparedCart.reduce((t, i) => t + (i.price * i.quantite), 0) / 100
  const grandTotal = cartTotal + preparedTotal

  return (
    <div className="order-screen">
      {modalProduct && (
        <div className="modal-overlay" onClick={() => setModalProduct(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <button className="modal__close" onClick={() => setModalProduct(null)}>×</button>
              <h2 className="modal__title">Personnalisation de l'article</h2>
            </div>

            <div className="modal__body">
              <div className="modal-section">
                {modalProduct.fields?.map((field, index) => (
                  <div key={index} className="field-group">
                    <h3 className="modal-section__title">{field.field_name}</h3>
                    {field.field_type === "selector" && (
                      <div className="option-list">
                        {field.options?.map((option, idx) => (
                          <button key={idx}
                            className={`option-btn ${fieldValues[field.field_name] === option ? 'option-btn--active' : ''}`}
                            onClick={() => setFieldValues(prev => ({ ...prev, [field.field_name]: option }))}
                          >{option}</button>
                        ))}
                      </div>
                    )}
                    {field.field_type === "quantity" && (
                      <input className="modal__input" type="number" value={fieldValues[field.field_name] ?? 0} min={0}
                        onChange={(e) => setFieldValues(prev => ({ ...prev, [field.field_name]: parseInt(e.target.value) || 0 }))} />
                    )}
                    {field.field_type === "checkbox" && (
                      <label className="checkbox-group">
                        <input type="checkbox" checked={fieldValues[field.field_name] ?? false}
                          onChange={(e) => setFieldValues(prev => ({ ...prev, [field.field_name]: e.target.checked }))} />
                        Activer
                      </label>
                    )}
                  </div>
                ))}
              </div>
              <div className="modal-section">
                <h3 className="modal-section__title">Note</h3>
                <textarea className="modal__textarea" placeholder="Informations complémentaires"
                  value={fieldValues["notes"] ?? ""}
                  onChange={(e) => setFieldValues(prev => ({ ...prev, notes: e.target.value }))} />
              </div>
            </div>

            <div className="modal__footer">
              <button className="confirm-btn"
                disabled={modalProduct.fields?.filter(f => f.category === "required").some(f => !fieldValues[f.field_name])}
                onClick={validerModal}>OK</button>
            </div>
          </div>
        </div>
      )}

      <div className="order-screen__main">
        <div className="order-screen__panel">
          <ItemList ajouterAuPanier={ajouterAuPanier} productMap={productMap} allCategories={allCategories} />
        </div>
      </div>

      <aside className="cart-panel">
        <div className="cart-panel__header">
          <h1 className="cart-panel__title">Table {tableNumber}</h1>
          <button className="back-btn" onClick={() => setScreen("floor_plan")}>← Retour</button>
        </div>

        <div className="cart-list">
          {cart.map(product =>
            <Item key={product.item_reference || product.uuid} product={product}
              ajouterAuPanier={ajouterAuPanier} supprimerDuPanier={supprimerDuPanier}
              prepared={false} setModalProduct={setModalProduct} setFieldValues={setFieldValues} />
          )}
          {cart.length > 0 && <button className="cart-prepare-btn" onClick={preparerLePanier}>Tout préparer</button>}
          {preparedCart.map(product =>
            <Item key={product.item_reference || product.uuid} product={product}
              ajouterAuPanier={ajouterAuPanier} supprimerDuPanier={supprimerDuPanier} prepared={true} />
          )}
        </div>

        <div className="cart-totals">
          <div className="cart-total-row"><span>Total TTC</span><span>{grandTotal.toFixed(2)} €</span></div>
          <div className="cart-total-row cart-total-row--muted"><span>En cours</span><span>+{cartTotal.toFixed(2)} €</span></div>
          {(cart.length > 0 || preparedCart.length > 0) && <button className="action-btn" onClick={viderLePanier}>Vider le panier</button>}
        </div>

        {paymentError && (
          <div className="cart-error">
            {paymentError}
          </div>
        )}

        <button
          className="checkout-btn"
          onClick={handleOpenPayment}
          disabled={cart.length === 0 && preparedCart.length === 0}
        >
          Règlement
        </button>
      </aside>
      {showPayModal && (
        <PayModal
          receiptUuid={receiptUuid}
          initialItems={payModalItems}
          onClose={handleClosePayment}
          onPaid={handlePaid}
          pdfFormat="A4"
          checkFormat={true}
        />
      )}
    </div>
  )
}

export default OrderScreen